import React, { useState } from 'react'

import { twMerge } from 'tailwind-merge'

import { Icon, IconProps } from '@components/Icon'
import { Dialog, DialogPanel, DialogBackdrop } from '@headlessui/react'
import { useTailwindBreakpoint } from '@hooks/useTailwindBreakpoint'

export interface ImageProps {
  title?: string
  src: string
  thumbnail?: string
  srcset?: object
  alt?: string
  icon?: IconProps
  className?: string
  focalPoint?: {
    x: string
    y: string
  }
  isMobile?: boolean
  zoomable?: boolean
  url?: string
}

export const Image: React.FC<ImageProps> = ({
  title = '',
  src,
  srcset,
  alt = '',
  icon,
  className = '',
  focalPoint = { x: '50%', y: '50%' },
  isMobile,
  zoomable = false,
}) => {
  const [isOpen, setIsOpen] = useState<boolean>(false)

  if (isMobile === undefined) {
    isMobile = useTailwindBreakpoint('md-')
  }

  let srcsetString: string | null = null
  if (srcset) {
    srcsetString = Object.entries(srcset)
      .map(([width, url]) => {
        const numericPart = width.match(/w(\d+)/)?.[1] || width.substring(1)
        return `${url} ${numericPart}w`
      })
      .join(', ')
  }

  return (
    <>
      {src && (
        <>
          {zoomable && (
            <Dialog
              open={isOpen}
              onClose={() => setIsOpen(false)}
              className="relative z-[9999] cursor-zoom-out"
            >
              <DialogBackdrop
                transition
                className="fixed inset-0 bg-black/30"
              />
              <DialogPanel
                onClick={() => setIsOpen(false)}
                transition
                className="fixed inset-0 flex w-screen items-center justify-center p-4 duration-300 ease-out data-[closed]:scale-90 data-[closed]:opacity-0"
              >
                <img
                  src={src}
                  srcSet={srcsetString || undefined}
                  alt={alt || title}
                  loading="lazy"
                  className="w-full h-full object-contain"
                />
              </DialogPanel>
            </Dialog>
          )}
          <img
            onClick={zoomable ? () => setIsOpen(true) : undefined}
            src={src}
            srcSet={srcsetString || undefined}
            alt={alt || title}
            loading="lazy"
            className={twMerge(
              `w-full h-full object-cover ${className} ${
                zoomable ? 'cursor-zoom-in' : ''
              }`,
            )}
            style={{
              objectPosition: `${focalPoint.x} ${focalPoint.y}`,
            }}
          />
          {icon?.name && (
            <div
              className={twMerge(
                `absolute bottom-4 left-4 z-10 ${icon.className}`,
              )}
            >
              <Icon
                name={icon.name}
                size={icon.size || (isMobile ? '12' : '24')}
              />
            </div>
          )}
        </>
      )}
    </>
  )
}

export default Image
